import { useCallback, useEffect, useState } from "react";
import {
    AlertColor,
    Button,
    Box,
    Paper,
    Table,
    TableContainer,
    TablePagination,
    Tabs,
    Tab,
    Typography,
} from "@mui/material";
import { FormAlert, FormSnackbar } from "../../../../components/Shared/Form";
import { useTemplatesDataStore } from "../../../../config/admin.stores";
import adminService from "../../../../services/admin.service";
import LoadingOverlay from "../../../../components/Shared/LoadingOverlay";
import Header from "../../../../components/Admin/Templates/Header/index";
import Rows from "../../../../components/Admin/Templates/Rows";
import CreateTemplate from "../../../../components/Admin/Templates/CreateTemplate";
import { Template } from "../../../../types/template";

const AdminTemplates = () => {
    const [tab, setTab] = useState(0);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [openEditTemplate, setOpenEditTemplate] = useState(false);
    const [editTemplate, setEditTemplate] = useState<Template | undefined>(undefined);

    const [alert, setAlert] = useState(false);
    const [alertId, setAlertId] = useState<number | null>(null);

    const [snackbar, setSnackbar] = useState("");
    const [severity, setSeverity] = useState<AlertColor>("success");

    const handleTabChange = (_event: any, newValue: number) => {
        setTab(newValue);
        setPage(0);
    };

    const templates = useTemplatesDataStore((state) => state.templates);
    const setTemplates = useTemplatesDataStore((state) => state.setTemplates);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        const response = await adminService.queryTemplate();
        setIsLoading(false);
        setTemplates(response);
    }, [setTemplates])

    useEffect(() => {
        if (templates.length === 0) {
            fetchData();
        }
        // eslint-disable-next-line
    }, []);

    const handleOpenTemplateEditor = (t: Template | undefined) => {
        setEditTemplate(t);
        setOpenEditTemplate(true);
    }

    const showAlert = (id: number) => {
        setAlertId(id);
        setAlert(true);
    }

    async function handleArchive(id: number | null) {
        if (id) {
            setIsLoading(true);
            const response = await adminService.archiveTemplate(id);
            if (response) {
                fetchData();
                setSeverity("success");
                setSnackbar("Template archived.");
                setAlert(false);
            } else {
                setIsLoading(false);
                setSeverity("error");
                setSnackbar("There was an error archiving the template.");
            }
        }
    }

    function createTable(status: string) {
        const data = templates.filter(
            template => status === "active" ? !template.archivedAt : template.archivedAt
        );

        return (
            <>
                <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
                    <Table sx={{ minWidth: 1100 }}>
                        <Header status={status} />
                        <Rows
                            data={data ?? []}
                            status={status}
                            page={page}
                            showAlert={showAlert}
                            showEditButton={status === "active"}
                            handleOpenTemplateEditor={handleOpenTemplateEditor}
                        />
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data?.length ?? 0}
                    rowsPerPage={5}
                    rowsPerPageOptions={[]}
                    page={page}
                    onPageChange={(_event, newPage) => setPage(newPage)}
                />
            </>
        )
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'left',
                gap: 3,
                margin: 3,
            }}
        >
            <LoadingOverlay open={isLoading} />
            <Box display="flex" justifyContent="space-between" sx={{ alignItems: "center", position: 'relative' }}>
                <Typography variant="h5">Manage Templates</Typography>
                <Box position='absolute' right={0}>
                    <Button
                        variant="contained"
                        onClick={() => { handleOpenTemplateEditor(undefined) }}
                        sx={{ marginRight: 1 }}
                    >
                        Create New Template
                    </Button>
                    <CreateTemplate
                        fetchData={fetchData}
                        template={editTemplate}
                        open={openEditTemplate}
                        setOpen={setOpenEditTemplate}
                    />
                </Box>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={handleTabChange}>
                    <Tab label="Active" id="active-tab" aria-controls="active-tabpanel"/>
                    <Tab label="Archived" id="archived-tab" aria-controls="archived-tabpanel"/>
                </Tabs>
            </Box>
            <div role="tabpanel" hidden={tab !== 0} id="active-tabpanel" aria-labelledby="active-tab">
                {tab === 0 && createTable("active")}
            </div>
            <div role="tabpanel" hidden={tab !== 1} id="archived-tabpanel" aria-labelledby="archived-tab">
                {tab === 1 && createTable("archived")}
            </div>
            <FormAlert
                label="admin-template"
                open={alert}
                setOpen={setAlert}
                disabled={isLoading}
                description={"Archive this template? This cannot be undone."}
                handleAgree={() => handleArchive(alertId)}
                agreeText={"Archive Template"}
            />
            <FormSnackbar message={snackbar} setMessage={setSnackbar} severity={severity} />
        </Box>
    );
}

export default AdminTemplates;
